import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchChurchTagAsync, fetchDistrictTagAsync, fetchNationTagAsync } from "@features/report/tag/services/reportTagSvc";
import { ReportTag, ReportTagListQuery, ReportTagListResult } from "../types/reportTag";
import { fetchTagStatusAsync } from "@features/report/tag/services/reportTagSvc";
import { ReportTagStatus, ReportTagStatusListResult } from "../types/reportTagStatus";

export function useReportTags(params: ReportTagListQuery): ReportTagListResult {
    const [current_page, setCurrentPage] = useState(params.page || 1);
    const [data, setData] = useState<ReportTag[]>([]);
    const [first_page_url, setFirstPageUrl] = useState<string | undefined>(undefined);
    const [from, setFrom] = useState<number | undefined>(undefined);
    const [last_page, setLastPage] = useState<number | undefined>(undefined);
    const [last_page_url, setLastPageUrl] = useState<string | undefined>(undefined);
    const [links, setLinks] = useState<{ url: string | null; label: string; active: boolean }[] | undefined>(undefined);
    const [next_page_url, setNextPageUrl] = useState<string | null | undefined>(undefined);
    const [path, setPath] = useState<string | undefined>(undefined);
    const [per_page, setPerPage] = useState<number | undefined>(undefined);
    const [prev_page_url, setPrevPageUrl] = useState<string | null | undefined>(undefined);
    const [to, setTo] = useState<number | undefined>(undefined);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    // Resolve scope once (church/district/nation)
    const scope = useMemo(() => {
        if (params.churchId) return { type: "church" as const, id: params.churchId };
        if (params.districtId) return { type: "district" as const, id: params.districtId };
        if (params.nationId) return { type: "nation" as const, id: params.nationId };
        return null;
    }, [params.churchId, params.districtId, params.nationId]);

    const queryParams = useMemo(() => {
        return {
            page: params.page,
            perPage: params.perPage,
            all: params.all,
            freetext: params.freetext,
            reportTagName: params.reportTagName,
            reportTagId: params.reportTagId,
            description: params.description,
            since: params.since,
            until: params.until,
            reportTagStatus: params.reportTagStatus,
        };
    }, [
        params.page,
        params.perPage,
        params.all,
        params.freetext,
        params.reportTagName,
        params.reportTagId,
        params.description,
        params.since,
        params.until,
        params.reportTagStatus,
    ]);

    const reload = useCallback(async () => {
        if (!scope?.id) {
            setLoading(false);
            setData([]);
            setTotal(0);
            return;
        }

        setLoading(true);

        try {
            let fetchFn:
                | typeof fetchChurchTagAsync
                | typeof fetchDistrictTagAsync
                | typeof fetchNationTagAsync;

            if (scope.type === "church") fetchFn = fetchChurchTagAsync;
            else if (scope.type === "district") fetchFn = fetchDistrictTagAsync;
            else fetchFn = fetchNationTagAsync;

            const response = await fetchFn(scope.id, queryParams);
            if (response) {
                const d = response.getData();
                setCurrentPage(d?.current_page || 1);
                setData(d?.data || []);
                setFirstPageUrl(d?.first_page_url);
                setFrom(d?.from);
                setLastPage(d?.last_page);
                setLastPageUrl(d?.last_page_url);
                setLinks(d?.links);
                setNextPageUrl(d?.next_page_url);
                setPath(d?.path);
                setPerPage(d?.per_page);
                setPrevPageUrl(d?.prev_page_url);
                setTo(d?.to);
                setTotal(d?.total || 0);
            }
        } finally {
            setLoading(false);
        }
    }, [queryParams, scope]);

    // Automatically reload when any of the params change
    useEffect(() => {
        void reload();
    }, [reload]);

    // Return the pagination state and reload function
    return { data, total, current_page, per_page, last_page, loading, reload };
}


export function useReportTagStatus(params: any): ReportTagStatusListResult {
    const [data, setData] = useState<ReportTagStatus[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    // Depend on the param contents, not the object identity. Call sites pass a
    // fresh literal each render (e.g. `{ all: true }`); keying off identity here
    // would make `reload` change every render and loop the reload effect forever.
    const statusParamsKey = JSON.stringify(params ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const statusParams = useMemo(() => params ?? {}, [statusParamsKey]);
    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchTagStatusAsync(statusParams);
            const d = response?.getData?.();
            setData(d?.data || []);
            setTotal(d?.total || 0);
        } finally {
            setLoading(false);
        }
    }, [statusParams]);

    useEffect(() => {
        reload();
    }, [reload]);

    return { total, data, loading, reload };
}